import React, { useState, useRef } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Image, Smile, Calendar, MapPin, Loader2, X } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

interface ComposeBoxProps {
  onPostSuccess?: () => void;
  replyToEventId?: string;
  replyToPubkey?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

export function ComposeBox({ 
  onPostSuccess, 
  replyToEventId, 
  replyToPubkey, 
  placeholder = "What's happening?!",
  autoFocus = false
}: ComposeBoxProps) {
  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey ?? '');
  const metadata = author.data?.metadata;
  const { mutate: publish, isPending: isPublishing } = useNostrPublish();
  const fileUpload = useUploadFile();
  const { toast } = useToast();

  const [content, setContent] = useState('');
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const characterLimit = 280;
  const charsRemaining = characterLimit - content.length;
  const isOverLimit = charsRemaining < 0;

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      toast({ title: 'Uploading', description: 'Sending image to media server...' });
      
      const uploadResult = await fileUpload.mutateAsync({ file });
      if (uploadResult?.url) {
        setMediaUrls(prev => [...prev, uploadResult.url]);
        toast({ title: 'Upload Complete', description: 'Media uploaded successfully!' });
      }
    } catch (error) {
      console.error(error);
      toast({ title: 'Upload Failed', description: 'Could not upload media. Please try again or use plain URLs.', variant: 'destructive' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveMedia = (indexToRemove: number) => {
    setMediaUrls(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handlePost = () => {
    if (!user) {
      toast({ title: 'Sign-in Required', description: 'Log in using the account switcher to make a post!' });
      return;
    }

    if (!content.trim() && mediaUrls.length === 0) {
      toast({ title: 'Empty Content', description: 'Cannot publish an empty post!' });
      return;
    }

    if (isOverLimit) {
      toast({ title: 'Character Limit Exceeded', description: 'Please trim your post to under 280 characters.' });
      return;
    }

    // Append uploaded media URLs to content
    let finalContent = content;
    if (mediaUrls.length > 0) {
      finalContent += '\n' + mediaUrls.join(' ');
    }

    // Compile Nostr tags (e.g. Reply NIP-10 headers if replying)
    const tags: string[][] = [['client', 'Twittstr']];
    if (replyToEventId) {
      tags.push(['e', replyToEventId, '', 'reply']);
    }
    if (replyToPubkey) {
      tags.push(['p', replyToPubkey]);
    }

    // Capture standard hashtag tags client-side
    const hashtagRegex = /#([\p{L}\p{N}_]+)/gu;
    let match;
    const addedTags = new Set<string>();
    while ((match = hashtagRegex.exec(content)) !== null) {
      const tag = match[1].toLowerCase();
      if (!addedTags.has(tag)) {
        tags.push(['t', tag]);
        addedTags.add(tag);
      }
    }

    publish({
      kind: 1,
      content: finalContent,
      tags
    }, {
      onSuccess: () => {
        setContent('');
        setMediaUrls([]);
        toast({ title: 'Posted successfully ✨', description: 'Your post is broad-casted onto Nostr relays!' });
        if (onPostSuccess) onPostSuccess();
      },
      onError: (err) => {
        toast({ title: 'Post Failed', description: 'relay networks could not receive the event: ' + err.message, variant: 'destructive' });
      }
    });
  };

  return (
    <div className="flex gap-3 w-full border-b border-border/80 pb-4">
      {/* Avatar Container */}
      <div className="shrink-0 pt-1">
        <img 
          src={metadata?.picture || `https://robohash.org/${user?.pubkey ?? 'unknown'}.png?set=set4`}
          alt="Current User Avatar" 
          className="size-10 rounded-full object-cover border border-border"
        />
      </div>

      {/* Inputs, Thumbnails, Action Toolbar, Counter ring & Post button container */}
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <Textarea
          placeholder={placeholder}
          className="w-full text-lg resize-none min-h-[80px] bg-transparent border-none p-0 focus-visible:ring-0 text-foreground leading-normal"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          autoFocus={autoFocus}
          maxLength={1000}
        />

        {/* Uploaded Media Previews thumbnail grid */}
        {mediaUrls.length > 0 && (
          <div className="grid grid-cols-2 gap-2 my-1 max-h-60 overflow-hidden rounded-2xl border border-border relative">
            {mediaUrls.map((url, index) => (
              <div key={index} className="relative group max-h-48 overflow-hidden bg-muted">
                <img src={url} alt="Attached Media Thumbnail" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => handleRemoveMedia(index)}
                  className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-black/80 rounded-full text-white cursor-pointer"
                >
                  <X className="size-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border/50 pt-2.5 mt-1">
          {/* Action Tools */}
          <div className="flex items-center gap-1.5 -ml-2">
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleMediaUpload} 
              accept="image/*,video/*" 
              className="hidden" 
            />
            
            <button
              type="button"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              className="p-2 hover:bg-sky-500/10 text-sky-500 rounded-full transition-all cursor-pointer disabled:opacity-50"
              title="Add photos/videos"
            >
              {isUploading ? <Loader2 className="size-5 animate-spin" /> : <Image className="size-5" />}
            </button>

            <button
              type="button"
              className="p-2 hover:bg-sky-500/10 text-sky-500 rounded-full transition-all cursor-not-allowed opacity-50"
              title="Add emojis"
            >
              <Smile className="size-5" />
            </button>
          </div>

          {/* Character counter & Publish button */}
          <div className="flex items-center gap-3">
            {content.length > 0 && (
              <span className={`text-xs tabular-nums font-semibold ${isOverLimit ? 'text-rose-500 font-extrabold text-[13px]' : charsRemaining < 20 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                {charsRemaining}
              </span>
            )}
            
            <Button
              onClick={handlePost}
              disabled={isPublishing || isUploading || (!content.trim() && mediaUrls.length === 0)}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-[15px] px-5 h-9"
            >
              {isPublishing ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
